<template>
  <div class="erp-account-container">
    <!-- 时间筛选器 -->
    <div class="filter-header">
      <div class="filter-title">
        <i class="el-icon-date"></i>
        <span>统计时间</span>
      </div>
      <div class="filter-content">
        <el-date-picker
            v-model="dateRange"
            type="daterange"
            align="right"
            unlink-panels
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :picker-options="pickerOptions"
            value-format="yyyy-MM-dd"
            format="yyyy-MM-dd"
            @change="handleDateChange"
            size="small">
        </el-date-picker>
        <el-button type="primary" size="small" @click="handleSearch" icon="el-icon-search">查询</el-button>
        <el-button size="small" @click="handleReset">重置</el-button>
      </div>
    </div>

    <!-- 表格报表 -->
    <div class="erp-element-report">
      <el-table
          :data="flattenTableData"
          border
          :show-header="true"
          style="width: 100%;"
          :cell-style="cellStyle"
          :header-cell-style="headerCellStyle"
          :row-class-name="tableRowClassName"
      >
        <!-- 科目列 -->
        <el-table-column
            prop="name"
            label="科目"
            align="left"
            min-width="280">
          <template slot-scope="scope">
            <div
                :class="['subject-cell', scope.row.level === 1 ? 'parent-level' : 'child-level']"
                :style="{ textAlign: scope.row.level === 1 ? 'center' : 'right', paddingRight: scope.row.level === 2 ? '20px' : '0' }"
            >
              <span class="subject-text">
                <i v-if="scope.row.level === 1" class="el-icon-folder-opened" style="margin-right: 8px; color: #409EFF;"></i>
                <i v-else-if="scope.row.level === 2" class="el-icon-document" style="margin-right: 8px; color: #909399;"></i>
                <i v-else class="el-icon-coin" style="margin-right: 8px; color: #e6a23c;"></i>
                {{ scope.row.name }}
              </span>
            </div>
          </template>
        </el-table-column>

        <!-- 期初金额列 -->
        <el-table-column
            prop="openingBalance"
            label="期初金额（元）"
            align="right"
            min-width="180">
          <template slot-scope="scope">
            <div :class="['amount-cell', scope.row.amountClass]" style="text-align: right;">
              {{ formatAmount(scope.row.openingBalance) }}
            </div>
          </template>
        </el-table-column>

        <!-- 当期金额列 -->
        <el-table-column
            prop="currentPeriodAmount"
            label="当期金额（元）"
            align="right"
            min-width="180">
          <template slot-scope="scope">
            <div :class="['amount-cell', scope.row.amountClass]" style="text-align: right;">
              {{ formatAmount(scope.row.currentPeriodAmount) }}
            </div>
          </template>
        </el-table-column>

        <!-- 期末金额列 -->
        <el-table-column
            prop="endingBalance"
            label="期末金额（元）"
            align="right"
            min-width="180">
          <template slot-scope="scope">
            <div :class="['amount-cell', scope.row.amountClass]" style="text-align: right;">
              {{ formatAmount(scope.row.endingBalance) }}
            </div>
          </template>
        </el-table-column>

        <!-- 操作列 -->
        <el-table-column
            label="操作"
            align="center"
            width="160"
            fixed="right">
          <template slot-scope="scope">
            <!-- 普通详情按钮 -->
            <el-button
                v-if="shouldShowNormalDetail(scope.row)"
                type="text"
                size="small"
                @click="handleDetail(scope.row)">
              详情
            </el-button>

            <!-- 西药下拉选择 -->
            <el-dropdown
                v-if="scope.row.detailType === 'westernSelect'"
                @command="(cmd) => handleWesternCommand(cmd, scope.row)"
                trigger="click"
                size="small">
              <span class="el-dropdown-link">
                详情<i class="el-icon-arrow-down el-icon--right"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="ku">西药库</el-dropdown-item>
                <el-dropdown-item command="fang">西药房</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>

            <!-- 中药下拉选择 -->
            <el-dropdown
                v-if="scope.row.detailType === 'chineseSelect'"
                @command="(cmd) => handleChineseCommand(cmd, scope.row)"
                trigger="click"
                size="small">
              <span class="el-dropdown-link">
                详情<i class="el-icon-arrow-down el-icon--right"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="ku">中药库</el-dropdown-item>
                <el-dropdown-item command="fang">中药房</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>

            <span v-if="!shouldShowNormalDetail(scope.row) && !shouldShowDropdownDetail(scope.row)" class="no-action">-</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { selFinanceReport } from "@/api/OpeningBalance/OpeningBalance";
import dayjs from "dayjs";

export default {
  name: 'ERPAccountTable',
  data() {
    return {
      // 时间范围（年月日）
      dateRange: [
        dayjs().startOf('month').format('YYYY-MM-DD'),
        dayjs().endOf('month').format('YYYY-MM-DD')
      ],
      // 接口返回的原始数据（结构完全按照后端实际返回）
      apiData: {
        bank: [],
        medical: null,
        eisai: null,
        pay: null,
        debit: null,
        cash: null,
        tcmDrug: null,
        drug: null,
        gather: [],      // 数组格式，如 [{name:"支付宝",startMoney:0,scopeMoney:0,nowMoney:555}, ...]
        handsel: null,
        recahrge: null
      },
      // 时间选择器快捷选项
      pickerOptions: {
        shortcuts: [
          {
            text: '本月',
            onClick(picker) {
              const start = dayjs().startOf('month');
              const end = dayjs().endOf('month');
              picker.$emit('pick', [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]);
            }
          },
          {
            text: '上月',
            onClick(picker) {
              const start = dayjs().subtract(1, 'month').startOf('month');
              const end = dayjs().subtract(1, 'month').endOf('month');
              picker.$emit('pick', [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]);
            }
          },
          {
            text: '本季度',
            onClick(picker) {
              const start = dayjs().startOf('quarter');
              const end = dayjs().endOf('quarter');
              picker.$emit('pick', [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]);
            }
          },
          {
            text: '本年',
            onClick(picker) {
              const start = dayjs().startOf('year');
              const end = dayjs().endOf('year');
              picker.$emit('pick', [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]);
            }
          }
        ]
      }
    };
  },
  computed: {
    // 将原始数据转换为表格所需的扁平化结构（支持父级汇总和子级展开）
    flattenTableData() {
      const result = [];

      // ---------- 1. 现金 ----------
      if (this.apiData.cash) {
        result.push({
          id: 'cash',
          name: this.apiData.cash.name,
          openingBalance: this.apiData.cash.startMoney || 0,
          currentPeriodAmount: this.apiData.cash.scopeMoney || 0,
          endingBalance: this.apiData.cash.nowMoney || 0,
          level: 1,
          amountClass: 'asset-positive',
          remark: '库存现金',
          children: [],
          detailType: 'fundDetailStatistics',
          classId: 101
        });
      }

      // ---------- 2. 银行（支持多个子项，动态生成） ----------
      if (this.apiData.bank && this.apiData.bank.length > 0) {
        const bankChildren = this.apiData.bank.map((bank, index) => ({
          id: `bank_${index}`,
          name: bank.name,
          openingBalance: bank.startMoney || 0,
          currentPeriodAmount: bank.scopeMoney || 0,
          endingBalance: bank.nowMoney || 0,
          level: 2,
          amountClass: 'asset-positive',
          parentId: 'bank',
          parentName: '银行',
          remark: `${bank.name}账户`,
          detailType: 'fundDetailStatistics',
          classId: bank.id || bank.bankId || null,
          originalName: bank.name
        }));

        // 银行父级（合计）
        result.push({
          id: 'bank',
          name: '银行',
          openingBalance: bankChildren.reduce((sum, item) => sum + item.openingBalance, 0),
          currentPeriodAmount: bankChildren.reduce((sum, item) => sum + item.currentPeriodAmount, 0),
          endingBalance: bankChildren.reduce((sum, item) => sum + item.endingBalance, 0),
          level: 1,
          amountClass: 'asset-positive',
          hasChildren: true,
          children: bankChildren,
          detailType: null
        });
        // 展开子项
        result.push(...bankChildren);
      }

      // ---------- 3. 聚合支付（修正：支持数组，生成父级+子项） ----------
      if (this.apiData.gather && this.apiData.gather.length > 0) {
        const gatherChildren = this.apiData.gather.map((item, index) => ({
          id: `gather_${index}`,
          name: item.name,
          openingBalance: item.startMoney || 0,
          currentPeriodAmount: item.scopeMoney || 0,
          endingBalance: item.nowMoney || 0,
          level: 2,
          amountClass: 'asset-positive',
          parentId: 'gather',
          parentName: '聚合支付',
          remark: `${item.name}收款`,
          detailType: 'fundDetailStatistics',
          classId: item.classId || null
        }));

        // 聚合支付父级（合计）
        result.push({
          id: 'gather',
          name: '聚合支付',
          openingBalance: gatherChildren.reduce((sum, child) => sum + child.openingBalance, 0),
          currentPeriodAmount: gatherChildren.reduce((sum, child) => sum + child.currentPeriodAmount, 0),
          endingBalance: gatherChildren.reduce((sum, child) => sum + child.endingBalance, 0),
          level: 1,
          amountClass: 'asset-positive',
          hasChildren: true,
          children: gatherChildren,
          detailType: null
        });
        // 展开子项
        result.push(...gatherChildren);
      }

      // ---------- 4. 库存商品（西药、中药、卫材） ----------
      const inventoryChildren = [];

      if (this.apiData.drug) {
        inventoryChildren.push({
          id: 'drug',
          name: this.apiData.drug.name,
          openingBalance: this.apiData.drug.startMoney || 0,
          currentPeriodAmount: this.apiData.drug.scopeMoney || 0,
          endingBalance: this.apiData.drug.nowMoney || 0,
          level: 2,
          amountClass: 'asset-positive',
          parentId: 'inventory',
          parentName: '库存商品',
          remark: '西药及制剂',
          detailType: 'westernSelect'
        });
      }

      if (this.apiData.tcmDrug) {
        inventoryChildren.push({
          id: 'tcmDrug',
          name: this.apiData.tcmDrug.name,
          openingBalance: this.apiData.tcmDrug.startMoney || 0,
          currentPeriodAmount: this.apiData.tcmDrug.scopeMoney || 0,
          endingBalance: this.apiData.tcmDrug.nowMoney || 0,
          level: 2,
          amountClass: 'asset-positive',
          parentId: 'inventory',
          parentName: '库存商品',
          remark: '各类中草药',
          detailType: 'chineseSelect'
        });
      }

      if (this.apiData.eisai) {
        inventoryChildren.push({
          id: 'eisai',
          name: this.apiData.eisai.name,
          openingBalance: this.apiData.eisai.startMoney || 0,
          currentPeriodAmount: this.apiData.eisai.scopeMoney || 0,
          endingBalance: this.apiData.eisai.nowMoney || 0,
          level: 2,
          amountClass: 'asset-positive',
          parentId: 'inventory',
          parentName: '库存商品',
          remark: '医用卫生材料',
          detailType: 'medicalMaterial'
        });
      }

      if (inventoryChildren.length > 0) {
        result.push({
          id: 'inventory',
          name: '库存商品',
          openingBalance: inventoryChildren.reduce((sum, item) => sum + item.openingBalance, 0),
          currentPeriodAmount: inventoryChildren.reduce((sum, item) => sum + item.currentPeriodAmount, 0),
          endingBalance: inventoryChildren.reduce((sum, item) => sum + item.endingBalance, 0),
          level: 1,
          amountClass: 'asset-positive',
          hasChildren: true,
          children: inventoryChildren,
          detailType: null
        });
        result.push(...inventoryChildren);
      }

      // ---------- 5. 应收账款（应收医保款、应收挂账款） ----------
      const receivableChildren = [];

      if (this.apiData.medical) {
        receivableChildren.push({
          id: 'medical',
          name: this.apiData.medical.name,
          openingBalance: this.apiData.medical.startMoney || 0,
          currentPeriodAmount: this.apiData.medical.scopeMoney || 0,
          endingBalance: this.apiData.medical.nowMoney || 0,
          level: 2,
          amountClass: 'asset-positive',
          parentId: 'receivable',
          parentName: '应收账款',
          remark: '医保中心待结算款项',
          detailType: null
        });
      }

      if (this.apiData.debit) {
        receivableChildren.push({
          id: 'debit',
          name: this.apiData.debit.name,
          openingBalance: this.apiData.debit.startMoney || 0,
          currentPeriodAmount: this.apiData.debit.scopeMoney || 0,
          endingBalance: this.apiData.debit.nowMoney || 0,
          level: 2,
          amountClass: 'asset-positive',
          parentId: 'receivable',
          parentName: '应收账款',
          remark: '挂账未结算款项',
          detailType: 'onCredit'
        });
      }

      if (receivableChildren.length > 0) {
        result.push({
          id: 'receivable',
          name: '应收账款',
          openingBalance: receivableChildren.reduce((sum, item) => sum + item.openingBalance, 0),
          currentPeriodAmount: receivableChildren.reduce((sum, item) => sum + item.currentPeriodAmount, 0),
          endingBalance: receivableChildren.reduce((sum, item) => sum + item.endingBalance, 0),
          level: 1,
          amountClass: 'asset-positive',
          hasChildren: true,
          children: receivableChildren,
          detailType: null
        });
        result.push(...receivableChildren);
      }

      // ---------- 6. 应付账款 ----------
      if (this.apiData.pay) {
        result.push({
          id: 'pay',
          name: this.apiData.pay.name,
          openingBalance: this.apiData.pay.startMoney || 0,
          currentPeriodAmount: this.apiData.pay.scopeMoney || 0,
          endingBalance: this.apiData.pay.nowMoney || 0,
          level: 1,
          amountClass: 'liability-negative',
          remark: '应付账款',
          detailType: 'supplierStatis'
        });
      }

      // ---------- 7. 健康金 ----------
      if (this.apiData.handsel) {
        result.push({
          id: 'handsel',
          name: this.apiData.handsel.name,
          openingBalance: this.apiData.handsel.startMoney || 0,
          currentPeriodAmount: this.apiData.handsel.scopeMoney || 0,
          endingBalance: this.apiData.handsel.nowMoney || 0,
          level: 1,
          amountClass: 'liability-negative',
          remark: '健康金',
          detailType: null
        });
      }

      // ---------- 8. 预收账款 ----------
      if (this.apiData.recahrge) {
        result.push({
          id: 'recahrge',
          name: this.apiData.recahrge.name,
          openingBalance: this.apiData.recahrge.startMoney || 0,
          currentPeriodAmount: this.apiData.recahrge.scopeMoney || 0,
          endingBalance: this.apiData.recahrge.nowMoney || 0,
          level: 1,
          amountClass: 'liability-negative',
          remark: '预收账款',
          detailType: null
        });
      }

      return result;
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    // 将日期范围转换为带时分秒的格式
    formatDateRangeWithTime() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return { startTime: '', endTime: '' };
      }
      const startTime = dayjs(this.dateRange[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss');
      const endTime = dayjs(this.dateRange[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss');
      return { startTime, endTime };
    },

    // 加载报表数据
    async loadData() {
      try {
        const { startTime, endTime } = this.formatDateRangeWithTime();
        const params = {};
        if (startTime && endTime) {
          params.startDate = startTime;
          params.endDate = endTime;
        }
        console.log('请求参数:', params);
        const res = await selFinanceReport(params);
        if (res.code === 200) {
          this.apiData = res.data;
          console.log('加载数据成功:', this.apiData);
        } else {
          this.$message.error(res.message || '获取数据失败');
        }
      } catch (error) {
        console.error('加载数据失败:', error);
        this.$message.error('系统异常，请联系管理员');
      }
    },

    // 时间范围变化时自动查询
    handleDateChange() {
      this.loadData();
    },

    // 手动查询
    handleSearch() {
      this.loadData();
    },

    // 重置时间范围为当月
    handleReset() {
      this.dateRange = [
        dayjs().startOf('month').format('YYYY-MM-DD'),
        dayjs().endOf('month').format('YYYY-MM-DD')
      ];
      this.loadData();
    },

    // 判断是否显示普通详情按钮
    shouldShowNormalDetail(row) {
      const normalTypes = ['fundDetailStatistics', 'onCredit', 'supplierStatis', 'medicalMaterial'];
      return normalTypes.includes(row.detailType);
    },

    // 判断是否显示下拉详情按钮（西药、中药）
    shouldShowDropdownDetail(row) {
      const dropdownTypes = ['westernSelect', 'chineseSelect'];
      return dropdownTypes.includes(row.detailType);
    },

    // 西药下拉命令处理
    handleWesternCommand(command, row) {
      const { startTime, endTime } = this.formatDateRangeWithTime();
      const path = command === 'ku' ? '/DrugInventory/xyk' : '/DrugInventory/xyf';
      this.$router.push({
        path: path,
        query: { startTime: startTime, endTime: endTime }
      });
    },

    // 中药下拉命令处理
    handleChineseCommand(command, row) {
      const { startTime, endTime } = this.formatDateRangeWithTime();
      const path = command === 'ku' ? '/DrugInventory/zyk' : '/DrugInventory/zyf';
      this.$router.push({
        path: path,
        query: { startTime: startTime, endTime: endTime }
      });
    },

    // 格式化金额显示（保留两位小数）
    formatAmount(amount) {
      if (amount === null || amount === undefined) return '0.00';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0.00';
      return num.toFixed(2);
    },

    // 表格行类名（父级行背景色）
    tableRowClassName({ row }) {
      if (row.level === 1) {
        return 'parent-row';
      }
      return '';
    },

    // 单元格样式（动态设置字体、颜色、对齐等）
    cellStyle({ row, column }) {
      const styles = {
        padding: '12px 8px',
        borderBottom: '1px solid #EBEEF5'
      };

      // 科目列样式
      if (column.property === 'name') {
        if (row.level === 1) {
          styles.fontWeight = 'bold';
          styles.backgroundColor = '#fafbfe';
          styles.fontSize = '14px';
          styles.textAlign = 'center';
        } else {
          styles.fontSize = '13px';
          styles.textAlign = 'right';
          styles.paddingRight = '20px';
        }
      }

      // 金额列样式
      if (['openingBalance', 'currentPeriodAmount', 'endingBalance'].includes(column.property)) {
        styles.fontFamily = 'Arial, sans-serif';
        styles.fontSize = '13px';
        styles.textAlign = 'right';
        styles.paddingRight = '15px';

        if (row.level === 1) {
          styles.fontWeight = 'bold';
          styles.backgroundColor = '#fafbfe';
        }

        // 负债类科目显示为红色
        if (row.name === '应付账款' || row.name === '健康金' || row.name === '预收账款') {
          styles.color = '#f56c6c';
        } else {
          styles.color = '#67c23a';
        }
      }

      // 操作列样式
      if (column.label === '操作') {
        styles.textAlign = 'center';
        if (row.level === 1) {
          styles.backgroundColor = '#fafbfe';
        }
      }

      return styles;
    },

    // 表头样式
    headerCellStyle({ column }) {
      const styles = {
        background: '#409EFF',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '14px',
        padding: '12px 8px'
      };

      if (column.property === 'name') {
        styles.textAlign = 'center';
      } else if (['openingBalance', 'currentPeriodAmount', 'endingBalance'].includes(column.property)) {
        styles.textAlign = 'right';
        styles.paddingRight = '15px';
      } else if (column.label === '操作') {
        styles.textAlign = 'center';
      }

      return styles;
    },

    // 详情按钮点击处理（根据 detailType 跳转不同页面）
    handleDetail(row) {
      const { startTime, endTime } = this.formatDateRangeWithTime();
      const queryParams = { startTime, endTime };

      switch (row.detailType) {
        case 'fundDetailStatistics':
          if (row.classId) queryParams.classId = row.classId;
          if (row.name) queryParams.subjectName = row.name;
          this.$router.push({
            path: '/fundDetailStatistics/fundDetailStatistics',
            query: queryParams
          });
          break;
        case 'onCredit':
          this.$router.push({
            path: '/onCredit/onCreditList',
            query: queryParams
          });
          break;
        case 'supplierStatis':
          this.$router.push({
            path: '/SupplierStatis/SupplierStatis',
            query: queryParams
          });
          break;
        default:
          this.$router.push({
            path: '/fundDetailStatistics/fundDetailStatistics',
            query: queryParams
          });
          break;
      }
    }
  }
};
</script>

<style scoped lang="less">
.erp-account-container {
  background: white;
  border-radius: 8px;
  padding: 20px;
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 8px;
  flex-shrink: 0;
}

.filter-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #303133;

  i {
    font-size: 16px;
    color: #409EFF;
  }
}

.filter-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.erp-element-report {
  flex: 1;
  overflow: auto;
}

.subject-cell {
  display: flex;
  align-items: center;
  width: 100%;
}

.parent-level {
  font-weight: bold;
  color: #303133;
  justify-content: center;
}

.child-level {
  color: #606266;
  justify-content: flex-end;
}

.subject-text {
  font-size: 14px;
  display: inline-flex;
  align-items: center;
}

.amount-cell {
  font-family: 'Arial', sans-serif;
}

.asset-positive {
  color: #67c23a;
}

.liability-negative {
  color: #f56c6c;
}

.no-action {
  color: #c0c4cc;
  font-size: 14px;
}

.el-dropdown-link {
  cursor: pointer;
  color: #409EFF;
  font-size: 12px;
  user-select: none;
}

.el-dropdown-link:hover {
  color: #66b1ff;
}

.el-dropdown-link .el-icon-arrow-down {
  font-size: 12px;
}

.el-table /deep/ tbody tr:hover > td {
  background-color: #f5f7fa !important;
}

.parent-row {
  background-color: #fafbfe;
}

.el-table /deep/ .cell {
  line-height: 1.5;
}

@media (max-width: 768px) {
  .erp-account-container {
    padding: 12px;
  }

  .filter-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .filter-content {
    width: 100%;
    flex-wrap: wrap;
  }

  .el-table /deep/ .el-table__header-wrapper,
  .el-table /deep/ .el-table__body-wrapper {
    overflow-x: auto;
  }
}

.erp-account-container::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.erp-account-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.erp-account-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.erp-account-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>